export const en = {
  'permissions.title': 'Roles and permissions',
  'permissions.description': 'Choose what each role may do at this gym. A change reaches everyone with that role on their next request.',
  'permissions.adminHoldsAll': 'The gym admin always holds every permission and alone configures the gym: settings, integrations, roles, the audit log and allowed networks.',
  'permissions.common': 'Common switches',
  'permissions.commonHint': 'The switches gyms change most often. Each one saves straight away.',
  'permissions.all': 'All permissions',
  'permissions.allHint': 'Every permission, grouped by area. Tick and untick, then save once.',
  'permissions.search': 'Search permissions',
  'permissions.changedOnly': 'Changed from default',
  'permissions.permission': 'Permission',
  'permissions.role': 'Role',
  'permissions.noMatch': 'No permission matches.',
  'permissions.source.DEFAULT': 'The role’s default',
  'permissions.source.ABILITY': 'Set by a common switch',
  'permissions.source.OVERRIDE': 'Changed on this page',
  'permissions.unsavedCell': 'Not saved yet',
  'permissions.locked': 'Every role keeps this.',
  'permissions.requires': 'Needs: {list}',
  'permissions.shifts.row': 'Works in shifts',
  'permissions.shifts.rowHint': 'Held to the shift roster, as set for the gym.',
  'permissions.shifts.change': 'Change in Settings → Shifts',
  'permissions.shifts.yes': 'Held to their shifts',
  'permissions.shifts.no': 'Not held to shifts',
  'permissions.risk.money': 'Money',
  'permissions.risk.sensitive': 'Sensitive',
  'permissions.cell': '{permission} for {role}',
  'permissions.unsaved.one': '{count} unsaved change',
  'permissions.unsaved.other': '{count} unsaved changes',
  'permissions.discard': 'Discard',
  'permissions.save': 'Save',
  'permissions.saved': 'Permissions saved',
  'permissions.confirmTitle': 'Save these permissions?',
  'permissions.confirmRole': 'You are changing what the {role} role can do.',
  'permissions.confirmMoney': 'These give access to money:',
  'permissions.confirmSave': 'Save',
  'permissions.ticked': '{permission} needs {list}: ticked as well.',
  'permissions.unticked': '{list} cannot stay without {permission}: unticked as well.',
  'admin.apiError.PERMISSION_NOT_GRANTABLE': 'That permission cannot be given to or taken from that role.',
  'admin.apiError.PERMISSION_REQUIRES': 'A permission was ticked without one it needs. Tick both, or neither.',

  'permissions.group.gym': 'Gym',
  'permissions.group.staff': 'Staff accounts',
  'permissions.group.members': 'Members',
  'permissions.group.memberships': 'Memberships and plans',
  'permissions.group.payments': 'Payments',
  'permissions.group.desk': 'Front desk',
  'permissions.group.classes': 'Classes and bookings',
  'permissions.group.trainers': 'Trainers',
  'permissions.group.appointments': 'Appointments',
  'permissions.group.access': 'Doors and access',
  'permissions.group.menu': 'Menu',
  'permissions.group.bar': 'Bar, stock and balances',
  'permissions.group.campaigns': 'Campaigns',
  'permissions.group.leads': 'Leads',
  'permissions.group.targets': 'Sales targets',
  'permissions.group.corporate': 'Corporate clients',
  'permissions.group.website': 'Website and legal pages',
  'permissions.group.finance': 'Finance and expenses',
  'permissions.group.reports': 'Reports',
  'permissions.group.team': 'Team: leave, hours and shifts',
  'permissions.group.payroll': 'Payroll',
  'permissions.group.equipment': 'Equipment and maintenance',
  'permissions.group.tasks': 'Tasks',
  'permissions.group.knowledge': 'Knowledge base',
  'permissions.group.recognition': 'Recognition',
  'permissions.group.recruiting': 'Recruiting',
  'permissions.group.practice': 'Practice gym',

  'permission.gym.read': 'See the gym’s details',
  'permission.gym.read.hint': 'Name, branches, opening hours and the settings every screen needs.',
  'permission.gym.update': 'Change the gym’s settings',
  'permission.gym.update.hint': 'Opening hours, booking and freeze rules, payments, notifications and the rest of the gym’s settings.',
  'permission.locations.manage': 'Add and change branches',
  'permission.locations.manage.hint': 'Open a branch, change its address and hours, or close it.',
  'permission.integrations.read': 'See integrations',
  'permission.integrations.read.hint': 'Which bank, SMS, email and door systems the gym is connected to.',
  'permission.integrations.manage': 'Connect integrations',
  'permission.integrations.manage.hint': 'Connect and change the bank, SMS, email and door systems, API keys and webhooks.',
  'permission.audit.read': 'Read the audit log',
  'permission.audit.read.hint': 'Who changed what and when, across the whole gym.',
  'permission.roles.configure': 'Set what each role can do',
  'permission.roles.configure.hint': 'This page: the common switches and every permission of every role.',
  'permission.network.manage': 'Choose allowed networks',
  'permission.network.manage.hint': 'The internet addresses staff must work from to do more than look things up.',

  'permission.users.manage': 'Manage staff accounts',
  'permission.users.manage.hint': 'Invite people to the team, change their role and branches, and remove them.',

  'permission.members.read': 'See members',
  'permission.members.read.hint': 'Find members and open their profile, contact details, memberships and visits.',
  'permission.members.read_own': 'See own clients only',
  'permission.members.read_own.hint': 'Without “See members”: only the members this person trains or treats, from their sessions, packages and classes.',
  'permission.members.create': 'Add members',
  'permission.members.create.hint': 'Register a new member at the desk or in the admin.',
  'permission.members.update': 'Edit members',
  'permission.members.update.hint': 'Change a member’s details, photo, card and notes.',
  'permission.members.delete': 'Delete members',
  'permission.members.delete.hint': 'Remove a member and their personal data for good.',
  'permission.members.tags': 'Tag members',
  'permission.members.tags.hint': 'Put tags on members and take them off.',
  'permission.members.export': 'Export the member list',
  'permission.members.export.hint': 'Download the members list, as filtered, as a spreadsheet with every name, email and phone number in it.',
  'permission.tags.manage': 'Manage the tag list',
  'permission.tags.manage.hint': 'Add, rename, recolour and delete the tags staff put on members.',
  'permission.segments.manage': 'Save member segments',
  'permission.segments.manage.hint': 'Create and change saved lists of members, used to filter, export and aim campaigns.',

  'permission.memberships.read': 'See memberships',
  'permission.memberships.read.hint': 'A member’s plans, dates, visits left and freezes.',
  'permission.memberships.create': 'Sell memberships',
  'permission.memberships.create.hint': 'Sell a plan to a member and take the payment.',
  'permission.memberships.update': 'Change memberships',
  'permission.memberships.update.hint': 'Change a membership’s dates or plan, or end it early.',
  'permission.memberships.pause': 'Freeze memberships',
  'permission.memberships.pause.hint': 'Put a membership on hold within the plan’s freeze rules.',
  'permission.memberships.resume': 'Unfreeze memberships',
  'permission.memberships.resume.hint': 'End a freeze early so the membership runs again.',
  'permission.memberships.comp': 'Give free memberships',
  'permission.memberships.comp.hint': 'Give a membership without a payment, with a reason.',
  'permission.memberships.delete': 'Delete memberships',
  'permission.memberships.delete.hint': 'Delete a membership entered by mistake, with a reason, and restore it within 7 days. A paid one is refunded first.',
  'permission.plans.manage': 'Edit membership plans',
  'permission.plans.manage.hint': 'Create, change and archive plans: name, price, length, hours and classes.',

  'permission.payments.read': 'See payments',
  'permission.payments.read.hint': 'Every member’s payments, receipts and refunds.',
  'permission.payments.create': 'Take payments',
  'permission.payments.create.hint': 'Charge a member by card or payment link.',
  'permission.payments.record': 'Record payments taken outside Bulk',
  'permission.payments.record.hint': 'Record money taken in cash or on the card terminal.',
  'permission.payments.refund_request': 'Ask for refunds',
  'permission.payments.refund_request.hint': 'Send a refund request for someone who refunds to approve.',
  'permission.payments.refund': 'Refund payments',
  'permission.payments.refund.hint': 'Approve or refuse refund requests and refund a payment directly.',

  'permission.reception.use': 'Work the front desk',
  'permission.reception.use.hint': 'Look members up by card or name, check them in and use the desk screen.',
  'permission.access.open': 'Open doors',
  'permission.access.open.hint': 'Open a door or turnstile from the desk.',

  'permission.classes.read': 'See classes',
  'permission.classes.read.hint': 'The timetable, classes and sessions.',
  'permission.classes.create': 'Add any class',
  'permission.classes.create.hint': 'Create classes, timetable slots and sessions for any trainer.',
  'permission.classes.update': 'Change any class',
  'permission.classes.update.hint': 'Change any class, slot or session, whoever teaches it.',
  'permission.classes.delete': 'Delete classes',
  'permission.classes.delete.hint': 'Remove a class and its future sessions.',
  'permission.classes.own': 'Schedule their own classes',
  'permission.classes.own.hint': 'Add and change only the classes they teach.',
  'permission.bookings.read': 'See bookings',
  'permission.bookings.read.hint': 'Who has booked each session, and the waiting lists.',
  'permission.bookings.create': 'Book members into classes',
  'permission.bookings.create.hint': 'Add a member to a session or its waiting list.',
  'permission.bookings.cancel': 'Cancel bookings',
  'permission.bookings.cancel.hint': 'Take a member off a session.',
  'permission.rooms.manage': 'Manage rooms',
  'permission.rooms.manage.hint': 'Add, change and remove the rooms classes take place in.',

  'permission.trainers.read': 'See trainers',
  'permission.trainers.read.hint': 'The trainer roster and each trainer’s page.',
  'permission.trainers.manage': 'Keep the trainer roster',
  'permission.trainers.manage.hint': 'Add and deactivate trainers, and set their names, contacts, specialities and staff photo.',
  'permission.trainers.profile': 'Shape trainers’ website profiles',
  'permission.trainers.profile.hint': 'Website photo, names and bios, and who shows on the website in what order.',

  'permission.appointments.read': 'See appointments',
  'permission.appointments.read.hint': 'Every one-to-one session and each host’s weekly hours.',
  'permission.appointments.manage': 'Manage appointments',
  'permission.appointments.manage.hint': 'Book, move and cancel one-to-one sessions for anyone, and set appointment types and hours.',
  'permission.appointments.own': 'Run their own appointments',
  'permission.appointments.own.hint': 'See their own day of one-to-one sessions and set their own weekly hours.',
  'permission.appointments.book_for_member': 'Book members into own sessions',
  'permission.appointments.book_for_member.hint': 'Book a member into this person’s own one-to-one time. It is confirmed at once and the member is told.',
  'permission.appointments.book': 'Book appointments as a member',
  'permission.appointments.book.hint': 'A member books one-to-one sessions for themselves.',

  'permission.access.read': 'See door activity',
  'permission.access.read.hint': 'Who came in, when and through which door, and how the readers are.',
  'permission.access.manage': 'Manage doors and cards',
  'permission.access.manage.hint': 'Set up readers and doors, door rules and members’ cards.',

  'permission.menu.read': 'See the menu',
  'permission.menu.read.hint': 'Drinks, food and products with their prices.',
  'permission.menu.manage': 'Edit the menu',
  'permission.menu.manage.hint': 'Add, change, hide and remove items and categories.',

  'permission.bar.sell': 'Sell at the bar',
  'permission.bar.sell.hint': 'Ring up sales at the bar till.',
  'permission.bar.refund': 'Refund bar sales',
  'permission.bar.refund.hint': 'Give the money back for a bar sale.',
  'permission.bar.stock.read': 'See bar stock',
  'permission.bar.stock.read.hint': 'What is on the shelf at each branch.',
  'permission.bar.stock': 'Count stock',
  'permission.bar.stock.hint': 'Receive deliveries and count what is on the shelf.',
  'permission.bar.insights': 'See bar reports',
  'permission.bar.insights.hint': 'Bar sales, best sellers and busy hours.',
  'permission.purchasing.read': 'See suppliers and orders',
  'permission.purchasing.read.hint': 'Suppliers and purchase orders.',
  'permission.purchasing.receive': 'Receive orders',
  'permission.purchasing.receive.hint': 'Record what arrived against a purchase order.',
  'permission.purchasing.manage': 'Place orders',
  'permission.purchasing.manage.hint': 'Add suppliers, and create and send purchase orders.',
  'permission.wallet.read': 'See balances',
  'permission.wallet.read.hint': 'Members’ prepaid balances and their history.',
  'permission.wallet.top_up': 'Top up balances',
  'permission.wallet.top_up.hint': 'Take money at the desk and add it to a member’s balance.',
  'permission.wallet.correct': 'Correct balances',
  'permission.wallet.correct.hint': 'Fix a mistake in a member’s balance; the reason is kept with the change.',

  'permission.campaigns.read': 'See campaigns',
  'permission.campaigns.read.hint': 'Sales, offers and announcements, live and past.',
  'permission.campaigns.manage': 'Run campaigns',
  'permission.campaigns.manage.hint': 'Create, change and end discount campaigns and offers.',
  'permission.campaigns.email': 'Send campaigns by email',
  'permission.campaigns.email.hint': 'Email a live campaign to members who agreed to offers by email, through the gym’s own email account.',
  'permission.campaigns.sms': 'Send campaigns by SMS',
  'permission.campaigns.sms.hint': 'Text a live campaign to members who agreed to offers by SMS; each text is charged to the gym.',

  'permission.leads.read': 'See leads',
  'permission.leads.read.hint': 'Website enquiries and walk-ins with their timeline.',
  'permission.leads.manage': 'Work leads',
  'permission.leads.manage.hint': 'Add leads, move them through the stages, set follow-ups and turn them into members.',

  'permission.targets.read': 'See sales targets',
  'permission.targets.read.hint': 'Targets per branch and person, and progress against them.',
  'permission.targets.manage': 'Set sales targets',
  'permission.targets.manage.hint': 'Create and change monthly targets.',

  'permission.corporate.read': 'See corporate clients',
  'permission.corporate.read.hint': 'Companies with their quotes, employees and invoices.',
  'permission.corporate.manage': 'Manage corporate clients',
  'permission.corporate.manage.hint': 'Add companies, send quotes and invoices, and record their payments.',

  'permission.website.read': 'See the website editor',
  'permission.website.read.hint': 'The gym’s website pages and blocks, as drafts.',
  'permission.website.manage': 'Edit the website',
  'permission.website.manage.hint': 'Change pages, blocks, pictures and texts as a draft.',
  'permission.website.publish': 'Publish the website',
  'permission.website.publish.hint': 'Put website changes live for visitors.',
  'permission.content.read': 'See legal pages',
  'permission.content.read.hint': 'Terms, privacy and the other documents.',
  'permission.content.manage': 'Edit legal pages',
  'permission.content.manage.hint': 'Write and change those documents as drafts.',
  'permission.content.publish': 'Publish legal pages',
  'permission.content.publish.hint': 'Put terms, privacy and the other documents live.',

  'permission.finance.read': 'See finance',
  'permission.finance.read.hint': 'Income, the ledger, the daily report and finance charts for every branch.',
  'permission.finance.export': 'Export finance',
  'permission.finance.export.hint': 'Download finance data as files.',
  'permission.finance.manage': 'Manage finance tasks',
  'permission.finance.manage.hint': 'Set up each month’s finance tasks and deadlines, and mark them done.',
  'permission.finance.month.close': 'Close finance months',
  'permission.finance.month.close.hint': 'Close a month once it is over and its tasks are done.',
  'permission.finance.month.reopen': 'Reopen finance months',
  'permission.finance.month.reopen.hint': 'Open a closed month again to correct it.',
  'permission.finance.branch': 'See their branches’ takings',
  'permission.finance.branch.hint': 'The daily takings and cash of the branches they work at.',
  'permission.expenses.read': 'See expenses',
  'permission.expenses.read.hint': 'Expenses, receipts and profit.',
  'permission.expenses.manage': 'Record expenses',
  'permission.expenses.manage.hint': 'Add, change and void expenses and recurring expenses, and keep their categories.',
  'permission.expenses.cash': 'Pay expenses from the drawer',
  'permission.expenses.cash.hint': 'Pay small expenses in cash from the open desk drawer.',

  'permission.reports.read': 'See reports',
  'permission.reports.read.hint': 'Reports and chart boards.',
  'permission.reports.manage': 'Manage shared reports',
  'permission.reports.manage.hint': 'Change and remove reports and boards others have shared.',

  'permission.employees.read': 'See employee records',
  'permission.employees.read.hint': 'Staff records: contracts, contacts and documents.',
  'permission.employees.manage': 'Manage employee records',
  'permission.employees.manage.hint': 'Add and change staff records, contracts and documents.',
  'permission.leave.request': 'Request their own leave',
  'permission.leave.request.hint': 'Ask for holiday or sick leave.',
  'permission.leave.read': 'See everyone’s leave',
  'permission.leave.read.hint': 'The team’s leave calendar and balances.',
  'permission.leave.approve': 'Decide leave',
  'permission.leave.approve.hint': 'Approve or refuse the leave of the people below them.',
  'permission.leave.manage': 'Manage leave',
  'permission.leave.manage.hint': 'Record leave for anyone and set leave types and allowances.',
  'permission.timesheets.own': 'See their own hours',
  'permission.timesheets.own.hint': 'Their own timesheet.',
  'permission.timesheets.clock': 'Clock in and out',
  'permission.timesheets.clock.hint': 'Start and end their working time on the clock.',
  'permission.timesheets.manage': 'Manage timesheets',
  'permission.timesheets.manage.hint': 'Correct and approve the team’s hours.',
  'permission.shifts.own': 'See their own shifts',
  'permission.shifts.own.hint': 'Their own schedule, and asking to swap a shift.',
  'permission.shifts.manage': 'Manage schedules',
  'permission.shifts.manage.hint': 'Give people schedules, change single days and approve shift swaps.',
  'permission.onboarding.own': 'Do their own onboarding',
  'permission.onboarding.own.hint': 'Work through their own onboarding checklist.',
  'permission.onboarding.manage': 'Manage onboarding',
  'permission.onboarding.manage.hint': 'Set up checklists and follow everyone’s onboarding.',
  'permission.reviews.own': 'Read their own reviews and warnings',
  'permission.reviews.own.hint': 'Read the reviews and warnings shared with them, acknowledge each and add a comment.',
  'permission.reviews.manage': 'Run reviews and warnings',
  'permission.reviews.manage.hint': 'Review people and issue warnings at their branches, set the review criteria and see each person’s timeline.',

  'permission.payroll.read': 'See payroll',
  'permission.payroll.read.hint': 'Payroll runs and payslips.',
  'permission.payroll.manage': 'Prepare payroll',
  'permission.payroll.manage.hint': 'Build payroll runs, adjust lines and mark them paid.',
  'permission.payroll.approve': 'Approve payroll',
  'permission.payroll.approve.hint': 'Approve a payroll run so it can be paid.',
  'permission.employees.pay.read': 'See pay',
  'permission.employees.pay.read.hint': 'Each employee’s salary and pay terms.',
  'permission.employees.pay.manage': 'Change pay',
  'permission.employees.pay.manage.hint': 'Set salaries, rates and pay terms.',

  'permission.equipment.read': 'See equipment',
  'permission.equipment.read.hint': 'The register of machines and their QR labels.',
  'permission.equipment.manage': 'Manage equipment',
  'permission.equipment.manage.hint': 'Add, change and retire machines, and plan their service.',
  'permission.maintenance.report': 'Report a problem',
  'permission.maintenance.report.hint': 'Open a maintenance ticket when something breaks.',
  'permission.maintenance.manage': 'Handle maintenance',
  'permission.maintenance.manage.hint': 'Assign, work on and close maintenance tickets.',

  'permission.tasks.read': 'See every task',
  'permission.tasks.read.hint': 'The team’s task lists and who does what.',
  'permission.tasks.manage': 'Manage tasks',
  'permission.tasks.manage.hint': 'Create task lists, give out tasks and follow them up.',
  'permission.tasks.complete': 'Complete their own tasks',
  'permission.tasks.complete.hint': 'Tick off the tasks given to them.',

  'permission.kb.read': 'Read the knowledge base',
  'permission.kb.read.hint': 'The procedures and policies meant for their role.',
  'permission.kb.manage': 'Write the knowledge base',
  'permission.kb.manage.hint': 'Write, change and publish articles, and see who has read them.',

  'permission.recognition.manage': 'Run recognition',
  'permission.recognition.manage.hint': 'Set up awards and see the kudos the team gives.',
  'permission.kudos.send': 'Send kudos',
  'permission.kudos.send.hint': 'Thank a colleague with kudos.',

  'permission.recruiting.read': 'See recruiting',
  'permission.recruiting.read.hint': 'Openings, candidates and interviews.',
  'permission.recruiting.manage': 'Run recruiting',
  'permission.recruiting.manage.hint': 'Post openings, move candidates along and arrange interviews.',
  'permission.recruiting.interview': 'Interview candidates',
  'permission.recruiting.interview.hint': 'Be named as a candidate’s interviewer, and see only the candidates they are named for, with the CV.',

  'permission.practice.manage': 'Run the practice gym',
  'permission.practice.manage.hint': 'Build, reset and delete the made-up gym the staff train in.',
};

export const ka: Record<keyof typeof en, string> = {
  'permissions.title': 'როლები და უფლებები',
  'permissions.description': 'აირჩიეთ, რა შეუძლია თითოეულ როლს ამ დარბაზში. ცვლილება ამ როლის ყველა თანამშრომლისთვის მათი შემდეგი მოქმედებიდან ამოქმედდება.',
  'permissions.adminHoldsAll': 'დარბაზის ადმინისტრატორს ყოველთვის ყველა უფლება აქვს და მხოლოდ ის არეგულირებს დარბაზს: პარამეტრებს, ინტეგრაციებს, როლებს, აუდიტის ჟურნალსა და დაშვებულ ქსელებს.',
  'permissions.common': 'ძირითადი გადამრთველები',
  'permissions.commonHint': 'გადამრთველები, რომლებსაც დარბაზები ყველაზე ხშირად ცვლიან. თითოეული დაუყოვნებლივ ინახება.',
  'permissions.all': 'ყველა უფლება',
  'permissions.allHint': 'ყველა უფლება სფეროების მიხედვით. მონიშნეთ და მოხსენით, შემდეგ ერთხელ შეინახეთ.',
  'permissions.search': 'უფლებების ძებნა',
  'permissions.changedOnly': 'ნაგულისხმევისგან განსხვავებული',
  'permissions.permission': 'უფლება',
  'permissions.role': 'როლი',
  'permissions.noMatch': 'ასეთი უფლება არ მოიძებნა.',
  'permissions.source.DEFAULT': 'როლის ნაგულისხმევი',
  'permissions.source.ABILITY': 'დაყენებულია ძირითადი გადამრთველით',
  'permissions.source.OVERRIDE': 'შეცვლილია ამ გვერდზე',
  'permissions.unsavedCell': 'ჯერ არ არის შენახული',
  'permissions.locked': 'ეს ყველა როლს რჩება.',
  'permissions.requires': 'საჭიროა: {list}',
  'permissions.shifts.row': 'მუშაობს ცვლებში',
  'permissions.shifts.rowHint': 'ცვლის გრაფიკი მას ზღუდავს დარბაზის პარამეტრების მიხედვით.',
  'permissions.shifts.change': 'შეცვლა: პარამეტრები → ცვლები',
  'permissions.shifts.yes': 'ცვლა ზღუდავს',
  'permissions.shifts.no': 'ცვლა არ ზღუდავს',
  'permissions.risk.money': 'ფული',
  'permissions.risk.sensitive': 'მგრძნობიარე',
  'permissions.cell': '{permission} — როლისთვის „{role}“',
  'permissions.unsaved.one': '{count} შეუნახავი ცვლილება',
  'permissions.unsaved.other': '{count} შეუნახავი ცვლილება',
  'permissions.discard': 'გაუქმება',
  'permissions.save': 'შენახვა',
  'permissions.saved': 'უფლებები შენახულია',
  'permissions.confirmTitle': 'შევინახოთ ეს უფლებები?',
  'permissions.confirmRole': 'თქვენ ცვლით იმას, რისი გაკეთებაც შეუძლია როლს „{role}“.',
  'permissions.confirmMoney': 'ეს უფლებები ფულზე წვდომას იძლევა:',
  'permissions.confirmSave': 'შენახვა',
  'permissions.ticked': 'უფლებას „{permission}“ სჭირდება: {list}. ამიტომ ისინიც მოინიშნა.',
  'permissions.unticked': 'უფლებას „{permission}“ საჭიროებენ: {list}. ამიტომ მათი მონიშვნაც მოიხსნა.',
  'admin.apiError.PERMISSION_NOT_GRANTABLE': 'ეს უფლება ამ როლს ვერ მიეცემა და ვერ ჩამოერთმევა.',
  'admin.apiError.PERMISSION_REQUIRES': 'უფლება მოინიშნა მისთვის საჭირო უფლების გარეშე. მონიშნეთ ორივე ან არცერთი.',

  'permissions.group.gym': 'დარბაზი',
  'permissions.group.staff': 'პერსონალის ანგარიშები',
  'permissions.group.members': 'წევრები',
  'permissions.group.memberships': 'აბონემენტები და პაკეტები',
  'permissions.group.payments': 'გადახდები',
  'permissions.group.desk': 'მიმღები',
  'permissions.group.classes': 'ვარჯიშები და ჯავშნები',
  'permissions.group.trainers': 'ტრენერები',
  'permissions.group.appointments': 'ინდივიდუალური ვარჯიშები',
  'permissions.group.access': 'კარები და დაშვება',
  'permissions.group.menu': 'მენიუ',
  'permissions.group.bar': 'ბარი, მარაგი და ბალანსები',
  'permissions.group.campaigns': 'აქციები',
  'permissions.group.leads': 'ლიდები',
  'permissions.group.targets': 'გაყიდვების მიზნები',
  'permissions.group.corporate': 'კორპორატიული კლიენტები',
  'permissions.group.website': 'ვებსაიტი და იურიდიული გვერდები',
  'permissions.group.finance': 'ფინანსები და ხარჯები',
  'permissions.group.reports': 'ანგარიშები',
  'permissions.group.team': 'გუნდი: შვებულება, საათები და ცვლები',
  'permissions.group.payroll': 'ხელფასები',
  'permissions.group.equipment': 'აღჭურვილობა და მომსახურება',
  'permissions.group.tasks': 'დავალებები',
  'permissions.group.knowledge': 'ცოდნის ბაზა',
  'permissions.group.recognition': 'აღიარება',
  'permissions.group.recruiting': 'რეკრუტინგი',
  'permissions.group.practice': 'სავარჯიშო დარბაზი',

  'permission.gym.read': 'დარბაზის მონაცემების ნახვა',
  'permission.gym.read.hint': 'სახელი, ფილიალები, სამუშაო საათები და პარამეტრები, რომლებიც ყველა ეკრანს სჭირდება.',
  'permission.gym.update': 'დარბაზის პარამეტრების შეცვლა',
  'permission.gym.update.hint': 'სამუშაო საათები, ჯავშნისა და გაყინვის წესები, გადახდები, შეტყობინებები და დარბაზის დანარჩენი პარამეტრები.',
  'permission.locations.manage': 'ფილიალების დამატება და შეცვლა',
  'permission.locations.manage.hint': 'ფილიალის გახსნა, მისი მისამართისა და საათების შეცვლა ან დახურვა.',
  'permission.integrations.read': 'ინტეგრაციების ნახვა',
  'permission.integrations.read.hint': 'რომელ ბანკთან, SMS-ის, ელფოსტისა და კარების სისტემებთან არის დარბაზი დაკავშირებული.',
  'permission.integrations.manage': 'ინტეგრაციების მიერთება',
  'permission.integrations.manage.hint': 'ბანკის, SMS-ის, ელფოსტისა და კარების სისტემების მიერთება და შეცვლა, API გასაღებები და ვებჰუკები.',
  'permission.audit.read': 'აუდიტის ჟურნალის ნახვა',
  'permission.audit.read.hint': 'ვინ რა შეცვალა და როდის, მთელ დარბაზში.',
  'permission.roles.configure': 'როლების უფლებების მითითება',
  'permission.roles.configure.hint': 'ეს გვერდი: ძირითადი გადამრთველები და ყველა როლის ყველა უფლება.',
  'permission.network.manage': 'დაშვებული ქსელების არჩევა',
  'permission.network.manage.hint': 'ინტერნეტ-მისამართები, საიდანაც პერსონალმა უნდა იმუშაოს, რომ ნახვის გარდა სხვა რამეც შეძლოს.',

  'permission.users.manage': 'პერსონალის ანგარიშების მართვა',
  'permission.users.manage.hint': 'გუნდში ადამიანების მოწვევა, მათი როლისა და ფილიალების შეცვლა და მოხსნა.',

  'permission.members.read': 'წევრების ნახვა',
  'permission.members.read.hint': 'წევრების პოვნა და მათი პროფილის, საკონტაქტო მონაცემების, აბონემენტებისა და ვიზიტების გახსნა.',
  'permission.members.read_own': 'მხოლოდ საკუთარი კლიენტების ნახვა',
  'permission.members.read_own.hint': '„წევრების ნახვის“ გარეშე: მხოლოდ ის წევრები, ვისაც ეს ადამიანი ავარჯიშებს ან ემსახურება, მისი სესიებიდან, პაკეტებიდან და ვარჯიშებიდან.',
  'permission.members.create': 'წევრების დამატება',
  'permission.members.create.hint': 'ახალი წევრის რეგისტრაცია მიმღებთან ან ადმინში.',
  'permission.members.update': 'წევრების რედაქტირება',
  'permission.members.update.hint': 'წევრის მონაცემების, ფოტოს, ბარათისა და შენიშვნების შეცვლა.',
  'permission.members.delete': 'წევრების წაშლა',
  'permission.members.delete.hint': 'წევრისა და მისი პირადი მონაცემების სამუდამოდ წაშლა.',
  'permission.members.tags': 'წევრებისთვის ტეგების მიჩენა',
  'permission.members.tags.hint': 'წევრებზე ტეგების დადება და მოხსნა.',
  'permission.members.export': 'წევრთა სიის ექსპორტი',
  'permission.members.export.hint': 'წევრთა სიის, გაფილტრულის სახით, ჩამოტვირთვა ცხრილად, ყველა სახელით, ელფოსტითა და ტელეფონის ნომრით.',
  'permission.tags.manage': 'ტეგების სიის მართვა',
  'permission.tags.manage.hint': 'ტეგების დამატება, გადარქმევა, ფერის შეცვლა და წაშლა, რომლებსაც თანამშრომლები წევრებს ანიჭებენ.',
  'permission.segments.manage': 'წევრთა სეგმენტების შენახვა',
  'permission.segments.manage.hint': 'წევრთა შენახული სიების შექმნა და შეცვლა, რომლებიც გამოიყენება გასაფილტრად, ექსპორტისთვის და აქციების ადრესატების შესარჩევად.',

  'permission.memberships.read': 'აბონემენტების ნახვა',
  'permission.memberships.read.hint': 'წევრის პაკეტები, თარიღები, დარჩენილი ვიზიტები და გაყინვები.',
  'permission.memberships.create': 'აბონემენტების გაყიდვა',
  'permission.memberships.create.hint': 'წევრისთვის პაკეტის გაყიდვა და გადახდის მიღება.',
  'permission.memberships.update': 'აბონემენტების შეცვლა',
  'permission.memberships.update.hint': 'აბონემენტის თარიღების ან პაკეტის შეცვლა, ან ვადამდე დასრულება.',
  'permission.memberships.pause': 'აბონემენტების გაყინვა',
  'permission.memberships.pause.hint': 'აბონემენტის შეჩერება პაკეტის გაყინვის წესების ფარგლებში.',
  'permission.memberships.resume': 'აბონემენტების გაყინვის მოხსნა',
  'permission.memberships.resume.hint': 'გაყინვის ვადამდე დასრულება, რომ აბონემენტი ისევ მოქმედებდეს.',
  'permission.memberships.comp': 'უფასო აბონემენტების მიცემა',
  'permission.memberships.comp.hint': 'აბონემენტის მიცემა გადახდის გარეშე, მიზეზის მითითებით.',
  'permission.memberships.delete': 'აბონემენტების წაშლა',
  'permission.memberships.delete.hint': 'შეცდომით შეყვანილი აბონემენტის წაშლა მიზეზის მითითებით და აღდგენა 7 დღის განმავლობაში. გადახდილი აბონემენტი ჯერ უნდა დაბრუნდეს.',
  'permission.plans.manage': 'აბონემენტის პაკეტების რედაქტირება',
  'permission.plans.manage.hint': 'პაკეტების შექმნა, შეცვლა და დაარქივება: სახელი, ფასი, ხანგრძლივობა, საათები და ჯგუფური ვარჯიშები.',

  'permission.payments.read': 'გადახდების ნახვა',
  'permission.payments.read.hint': 'ყველა წევრის გადახდები, ქვითრები და დაბრუნებები.',
  'permission.payments.create': 'გადახდების მიღება',
  'permission.payments.create.hint': 'წევრისთვის თანხის ჩამოჭრა ბარათით ან გადახდის ბმულით.',
  'permission.payments.record': 'Bulk-ის გარეთ მიღებული გადახდების აღრიცხვა',
  'permission.payments.record.hint': 'ნაღდი ფულით ან საბარათე ტერმინალში მიღებული თანხის აღრიცხვა.',
  'permission.payments.refund_request': 'დაბრუნების მოთხოვნის გაგზავნა',
  'permission.payments.refund_request.hint': 'გადახდის დაბრუნების მოთხოვნა; მას ამტკიცებს ის, ვისაც დაბრუნება შეუძლია.',
  'permission.payments.refund': 'გადახდების დაბრუნება',
  'permission.payments.refund.hint': 'დაბრუნების მოთხოვნების დამტკიცება ან უარყოფა და გადახდის პირდაპირ დაბრუნება.',

  'permission.reception.use': 'მიმღებად მუშაობა',
  'permission.reception.use.hint': 'წევრების პოვნა ბარათით ან სახელით, მათი შემოშვება და მიმღების ეკრანით სარგებლობა.',
  'permission.access.open': 'კარების გახსნა',
  'permission.access.open.hint': 'კარის ან ტურნიკეტის გახსნა მიმღებიდან.',

  'permission.classes.read': 'ვარჯიშების ნახვა',
  'permission.classes.read.hint': 'ცხრილი, ვარჯიშები და სესიები.',
  'permission.classes.create': 'ნებისმიერი ვარჯიშის დამატება',
  'permission.classes.create.hint': 'ვარჯიშების, ცხრილის სლოტებისა და სესიების შექმნა ნებისმიერი ტრენერისთვის.',
  'permission.classes.update': 'ნებისმიერი ვარჯიშის შეცვლა',
  'permission.classes.update.hint': 'ნებისმიერი ვარჯიშის, სლოტისა და სესიის შეცვლა, ვინც არ უნდა ატარებდეს.',
  'permission.classes.delete': 'ვარჯიშების წაშლა',
  'permission.classes.delete.hint': 'ვარჯიშისა და მისი მომავალი სესიების წაშლა.',
  'permission.classes.own': 'საკუთარი ვარჯიშების დაგეგმვა',
  'permission.classes.own.hint': 'მხოლოდ იმ ვარჯიშების დამატება და შეცვლა, რომლებსაც თვითონ ატარებენ.',
  'permission.bookings.read': 'ჯავშნების ნახვა',
  'permission.bookings.read.hint': 'ვინ დაჯავშნა თითოეული სესია და მოლოდინის სიები.',
  'permission.bookings.create': 'წევრების ვარჯიშებზე დაჯავშნა',
  'permission.bookings.create.hint': 'წევრის სესიაზე ან მის მოლოდინის სიაში დამატება.',
  'permission.bookings.cancel': 'ჯავშნების გაუქმება',
  'permission.bookings.cancel.hint': 'წევრის სესიიდან მოხსნა.',
  'permission.rooms.manage': 'ოთახების მართვა',
  'permission.rooms.manage.hint': 'იმ ოთახების დამატება, შეცვლა და წაშლა, სადაც ვარჯიშები ტარდება.',

  'permission.trainers.read': 'ტრენერების ნახვა',
  'permission.trainers.read.hint': 'ტრენერების სია და თითოეული ტრენერის გვერდი.',
  'permission.trainers.manage': 'ტრენერების სიის მართვა',
  'permission.trainers.manage.hint': 'ტრენერების დამატება და დეაქტივაცია, მათი სახელების, კონტაქტების, სპეციალობებისა და თანამშრომლის ფოტოს მითითება.',
  'permission.trainers.profile': 'ტრენერების ვებპროფილების მართვა',
  'permission.trainers.profile.hint': 'ვებსაიტის ფოტო, სახელები და ბიოგრაფიები, ვინ ჩანს ვებსაიტზე და რა თანმიმდევრობით.',

  'permission.appointments.read': 'ჩანიშვნების ნახვა',
  'permission.appointments.read.hint': 'ყველა ინდივიდუალური სესია და თითოეული მასპინძლის კვირის საათები.',
  'permission.appointments.manage': 'ჩანიშვნების მართვა',
  'permission.appointments.manage.hint': 'ინდივიდუალური სესიების დაჯავშნა, გადატანა და გაუქმება ნებისმიერისთვის, ჩანიშვნის ტიპებისა და საათების მითითება.',
  'permission.appointments.own': 'საკუთარი ჩანიშვნების წარმართვა',
  'permission.appointments.own.hint': 'საკუთარი დღის ინდივიდუალური სესიების ნახვა და საკუთარი კვირის საათების მითითება.',
  'permission.appointments.book_for_member': 'წევრების ჩაწერა საკუთარ სესიებზე',
  'permission.appointments.book_for_member.hint': 'წევრის ჩაწერა ამ ადამიანის საკუთარ ინდივიდუალურ დროზე. ჯავშანი მაშინვე დასტურდება და წევრი შეტყობინებას იღებს.',
  'permission.appointments.book': 'ჩანიშვნის დაჯავშნა წევრის სახით',
  'permission.appointments.book.hint': 'წევრი თავად ჯავშნის ინდივიდუალურ სესიებს.',

  'permission.access.read': 'კარების აქტივობის ნახვა',
  'permission.access.read.hint': 'ვინ, როდის და რომელი კარით შემოვიდა და როგორ მუშაობს წამკითხველები.',
  'permission.access.manage': 'კარებისა და ბარათების მართვა',
  'permission.access.manage.hint': 'წამკითხველებისა და კარების მოწყობა, კარის წესები და წევრების ბარათები.',

  'permission.menu.read': 'მენიუს ნახვა',
  'permission.menu.read.hint': 'სასმელები, საჭმელი და პროდუქტები ფასებით.',
  'permission.menu.manage': 'მენიუს რედაქტირება',
  'permission.menu.manage.hint': 'პროდუქტებისა და კატეგორიების დამატება, შეცვლა, დამალვა და წაშლა.',

  'permission.bar.sell': 'ბარში გაყიდვა',
  'permission.bar.sell.hint': 'გაყიდვების გატარება ბარის სალაროში.',
  'permission.bar.refund': 'ბარის გაყიდვების დაბრუნება',
  'permission.bar.refund.hint': 'ბარში გაყიდულზე თანხის დაბრუნება.',
  'permission.bar.stock.read': 'ბარის მარაგის ნახვა',
  'permission.bar.stock.read.hint': 'რა არის თაროზე თითოეულ ფილიალში.',
  'permission.bar.stock': 'მარაგის აღწერა',
  'permission.bar.stock.hint': 'მიწოდებების მიღება და თაროზე არსებულის აღწერა.',
  'permission.bar.insights': 'ბარის ანგარიშების ნახვა',
  'permission.bar.insights.hint': 'ბარის გაყიდვები, ყველაზე გაყიდვადი პროდუქტები და დატვირთული საათები.',
  'permission.purchasing.read': 'მომწოდებლებისა და შეკვეთების ნახვა',
  'permission.purchasing.read.hint': 'მომწოდებლები და შესყიდვის შეკვეთები.',
  'permission.purchasing.receive': 'შეკვეთების მიღება',
  'permission.purchasing.receive.hint': 'შესყიდვის შეკვეთის მიხედვით მოსულის აღრიცხვა.',
  'permission.purchasing.manage': 'შეკვეთების გაკეთება',
  'permission.purchasing.manage.hint': 'მომწოდებლების დამატება, შესყიდვის შეკვეთების შექმნა და გაგზავნა.',
  'permission.wallet.read': 'ბალანსების ნახვა',
  'permission.wallet.read.hint': 'წევრების წინასწარ გადახდილი ბალანსები და მათი ისტორია.',
  'permission.wallet.top_up': 'ბალანსის შევსება',
  'permission.wallet.top_up.hint': 'მიმღებთან თანხის მიღება და წევრის ბალანსზე დამატება.',
  'permission.wallet.correct': 'ბალანსის შესწორება',
  'permission.wallet.correct.hint': 'წევრის ბალანსში შეცდომის გასწორება; მიზეზი ცვლილებასთან ერთად ინახება.',

  'permission.campaigns.read': 'აქციების ნახვა',
  'permission.campaigns.read.hint': 'ფასდაკლებები, შეთავაზებები და განცხადებები, მიმდინარე და წარსული.',
  'permission.campaigns.manage': 'აქციების მართვა',
  'permission.campaigns.manage.hint': 'ფასდაკლების აქციებისა და შეთავაზებების შექმნა, შეცვლა და დასრულება.',
  'permission.campaigns.email': 'აქციების ელფოსტით გაგზავნა',
  'permission.campaigns.email.hint': 'მიმდინარე აქციის გაგზავნა იმ წევრებთან, ვინც დათანხმდა შეთავაზებებს ელფოსტით, დარბაზის საკუთარი ელფოსტის ანგარიშიდან.',
  'permission.campaigns.sms': 'აქციების SMS-ით გაგზავნა',
  'permission.campaigns.sms.hint': 'მიმდინარე აქციის გაგზავნა იმ წევრებთან, ვინც დათანხმდა შეთავაზებებს SMS-ით; თითოეული SMS დარბაზის ხარჯია.',

  'permission.leads.read': 'ლიდების ნახვა',
  'permission.leads.read.hint': 'ვებსაიტიდან მოსული მოთხოვნები და უშუალოდ მოსულები მათი ქრონოლოგიით.',
  'permission.leads.manage': 'ლიდებთან მუშაობა',
  'permission.leads.manage.hint': 'ლიდების დამატება, ეტაპებზე გადაყვანა, შემდგომი კონტაქტების დაგეგმვა და წევრებად გადაქცევა.',

  'permission.targets.read': 'გაყიდვების მიზნების ნახვა',
  'permission.targets.read.hint': 'მიზნები ფილიალებისა და ადამიანების მიხედვით და მათი შესრულება.',
  'permission.targets.manage': 'გაყიდვების მიზნების დაწესება',
  'permission.targets.manage.hint': 'ყოველთვიური მიზნების შექმნა და შეცვლა.',

  'permission.corporate.read': 'კორპორატიული კლიენტების ნახვა',
  'permission.corporate.read.hint': 'კომპანიები მათი შეთავაზებებით, თანამშრომლებითა და ინვოისებით.',
  'permission.corporate.manage': 'კორპორატიული კლიენტების მართვა',
  'permission.corporate.manage.hint': 'კომპანიების დამატება, შეთავაზებებისა და ინვოისების გაგზავნა და მათი გადახდების აღრიცხვა.',

  'permission.website.read': 'ვებსაიტის რედაქტორის ნახვა',
  'permission.website.read.hint': 'დარბაზის ვებსაიტის გვერდები და ბლოკები, მონახაზის სახით.',
  'permission.website.manage': 'ვებსაიტის რედაქტირება',
  'permission.website.manage.hint': 'გვერდების, ბლოკების, სურათებისა და ტექსტების შეცვლა მონახაზის სახით.',
  'permission.website.publish': 'ვებსაიტის გამოქვეყნება',
  'permission.website.publish.hint': 'ვებსაიტზე შეტანილი ცვლილებების ვიზიტორებისთვის გამოქვეყნება.',
  'permission.content.read': 'იურიდიული გვერდების ნახვა',
  'permission.content.read.hint': 'წესები და პირობები, კონფიდენციალურობა და სხვა დოკუმენტები.',
  'permission.content.manage': 'იურიდიული გვერდების რედაქტირება',
  'permission.content.manage.hint': 'ამ დოკუმენტების დაწერა და შეცვლა მონახაზის სახით.',
  'permission.content.publish': 'იურიდიული გვერდების გამოქვეყნება',
  'permission.content.publish.hint': 'წესებისა და პირობების, კონფიდენციალურობის პოლიტიკისა და სხვა დოკუმენტების გამოქვეყნება.',

  'permission.finance.read': 'ფინანსების ნახვა',
  'permission.finance.read.hint': 'შემოსავალი, რეესტრი, დღის ანგარიში და ფინანსური დიაგრამები ყველა ფილიალისთვის.',
  'permission.finance.export': 'ფინანსების ექსპორტი',
  'permission.finance.export.hint': 'ფინანსური მონაცემების ფაილებად ჩამოტვირთვა.',
  'permission.finance.manage': 'ფინანსური დავალებების მართვა',
  'permission.finance.manage.hint': 'ყოველი თვის ფინანსური დავალებებისა და ვადების დაყენება და შესრულებულად მონიშვნა.',
  'permission.finance.month.close': 'ფინანსური თვის დახურვა',
  'permission.finance.month.close.hint': 'თვის დახურვა, როცა ის დასრულდება და მისი დავალებები შესრულებულია.',
  'permission.finance.month.reopen': 'ფინანსური თვეების ხელახლა გახსნა',
  'permission.finance.month.reopen.hint': 'დახურული თვის ხელახლა გახსნა გასასწორებლად.',
  'permission.finance.branch': 'თავისი ფილიალების შემოსავლის ნახვა',
  'permission.finance.branch.hint': 'იმ ფილიალების დღიური შემოსავალი და ნაღდი ფული, სადაც მუშაობენ.',
  'permission.expenses.read': 'ხარჯების ნახვა',
  'permission.expenses.read.hint': 'ხარჯები, ქვითრები და მოგება.',
  'permission.expenses.manage': 'ხარჯების აღრიცხვა',
  'permission.expenses.manage.hint': 'ხარჯებისა და განმეორებადი ხარჯების დამატება, შეცვლა და გაუქმება, მათი კატეგორიების წარმოება.',
  'permission.expenses.cash': 'ხარჯების გადახდა სალაროდან',
  'permission.expenses.cash.hint': 'მცირე ხარჯების ნაღდი ანგარიშსწორება ღია სალაროდან.',

  'permission.reports.read': 'ანგარიშების ნახვა',
  'permission.reports.read.hint': 'ანგარიშები და დიაგრამების დაფები.',
  'permission.reports.manage': 'გაზიარებული ანგარიშების მართვა',
  'permission.reports.manage.hint': 'სხვების მიერ გაზიარებული ანგარიშებისა და დაფების შეცვლა და წაშლა.',

  'permission.employees.read': 'თანამშრომლების ჩანაწერების ნახვა',
  'permission.employees.read.hint': 'პერსონალის ჩანაწერები: ხელშეკრულებები, კონტაქტები და დოკუმენტები.',
  'permission.employees.manage': 'თანამშრომლების ჩანაწერების მართვა',
  'permission.employees.manage.hint': 'პერსონალის ჩანაწერების, ხელშეკრულებებისა და დოკუმენტების დამატება და შეცვლა.',
  'permission.leave.request': 'საკუთარი შვებულების მოთხოვნა',
  'permission.leave.request.hint': 'შვებულების ან ავადმყოფობის დღეების მოთხოვნა.',
  'permission.leave.read': 'ყველას შვებულების ნახვა',
  'permission.leave.read.hint': 'გუნდის შვებულების კალენდარი და ნაშთები.',
  'permission.leave.approve': 'შვებულების გადაწყვეტა',
  'permission.leave.approve.hint': 'მათ დაქვემდებარებული ადამიანების შვებულების დამტკიცება ან უარყოფა.',
  'permission.leave.manage': 'შვებულების მართვა',
  'permission.leave.manage.hint': 'ნებისმიერისთვის შვებულების აღრიცხვა და შვებულების ტიპებისა და ნორმების დაყენება.',
  'permission.timesheets.own': 'საკუთარი საათების ნახვა',
  'permission.timesheets.own.hint': 'საკუთარი სამუშაო საათების ცხრილი.',
  'permission.timesheets.clock': 'მოსვლისა და წასვლის აღრიცხვა',
  'permission.timesheets.clock.hint': 'სამუშაო დროის დაწყება და დასრულება საათზე.',
  'permission.timesheets.manage': 'სამუშაო საათების მართვა',
  'permission.timesheets.manage.hint': 'გუნდის საათების გასწორება და დამტკიცება.',
  'permission.shifts.own': 'საკუთარი ცვლების ნახვა',
  'permission.shifts.own.hint': 'საკუთარი განრიგი და ცვლის გაცვლის მოთხოვნა.',
  'permission.shifts.manage': 'განრიგების მართვა',
  'permission.shifts.manage.hint': 'ადამიანებისთვის განრიგების მინიჭება, ცალკეული დღეების შეცვლა და ცვლების გაცვლის დამტკიცება.',
  'permission.onboarding.own': 'საკუთარი ადაპტაციის გავლა',
  'permission.onboarding.own.hint': 'საკუთარი ადაპტაციის ჩამონათვალის შესრულება.',
  'permission.onboarding.manage': 'ადაპტაციის მართვა',
  'permission.onboarding.manage.hint': 'ჩამონათვალების შედგენა და ყველას ადაპტაციის კონტროლი.',
  'permission.reviews.own': 'საკუთარი შეფასებებისა და გაფრთხილებების ნახვა',
  'permission.reviews.own.hint': 'მისთვის გაზიარებული შეფასებებისა და გაფრთხილებების წაკითხვა, დადასტურება და კომენტარის დართვა.',
  'permission.reviews.manage': 'შეფასებებისა და გაფრთხილებების მართვა',
  'permission.reviews.manage.hint': 'თანამშრომლების შეფასება და გაფრთხილება თავის ფილიალებში, კრიტერიუმების დაყენება და თითოეულის ისტორიის ნახვა.',

  'permission.payroll.read': 'ხელფასების ნახვა',
  'permission.payroll.read.hint': 'სახელფასო უწყისები და სახელფასო ფურცლები.',
  'permission.payroll.manage': 'ხელფასების მომზადება',
  'permission.payroll.manage.hint': 'სახელფასო უწყისების შედგენა, სტრიქონების შესწორება და გადახდილად მონიშვნა.',
  'permission.payroll.approve': 'ხელფასების დამტკიცება',
  'permission.payroll.approve.hint': 'სახელფასო უწყისის დამტკიცება, რომ ის გადახდილ იქნეს.',
  'permission.employees.pay.read': 'ანაზღაურების ნახვა',
  'permission.employees.pay.read.hint': 'თითოეული თანამშრომლის ხელფასი და ანაზღაურების პირობები.',
  'permission.employees.pay.manage': 'ანაზღაურების შეცვლა',
  'permission.employees.pay.manage.hint': 'ხელფასების, განაკვეთებისა და ანაზღაურების პირობების დაყენება.',

  'permission.equipment.read': 'აღჭურვილობის ნახვა',
  'permission.equipment.read.hint': 'ტრენაჟორების რეესტრი და მათი QR ეტიკეტები.',
  'permission.equipment.manage': 'აღჭურვილობის მართვა',
  'permission.equipment.manage.hint': 'ტრენაჟორების დამატება, შეცვლა და ჩამოწერა, მათი მომსახურების დაგეგმვა.',
  'permission.maintenance.report': 'პრობლემის შეტყობინება',
  'permission.maintenance.report.hint': 'ტექნიკური მომსახურების განაცხადის გახსნა, როცა რამე გაფუჭდება.',
  'permission.maintenance.manage': 'ტექნიკური მომსახურების წარმართვა',
  'permission.maintenance.manage.hint': 'ტექნიკური მომსახურების განაცხადების მინიჭება, შესრულება და დახურვა.',

  'permission.tasks.read': 'ყველა დავალების ნახვა',
  'permission.tasks.read.hint': 'გუნდის დავალებების სიები და ვინ რას აკეთებს.',
  'permission.tasks.manage': 'დავალებების მართვა',
  'permission.tasks.manage.hint': 'დავალებების სიების შექმნა, დავალებების გაცემა და მათი კონტროლი.',
  'permission.tasks.complete': 'საკუთარი დავალებების შესრულება',
  'permission.tasks.complete.hint': 'მათთვის მიცემული დავალებების მონიშვნა შესრულებულად.',

  'permission.kb.read': 'ცოდნის ბაზის კითხვა',
  'permission.kb.read.hint': 'პროცედურები და წესები, რომლებიც მათი როლისთვისაა განკუთვნილი.',
  'permission.kb.manage': 'ცოდნის ბაზის წერა',
  'permission.kb.manage.hint': 'სტატიების დაწერა, შეცვლა და გამოქვეყნება და იმის ნახვა, ვინ წაიკითხა ისინი.',

  'permission.recognition.manage': 'აღიარების მართვა',
  'permission.recognition.manage.hint': 'ჯილდოების დაყენება და გუნდის მიერ გაცემული მადლობების ნახვა.',
  'permission.kudos.send': 'მადლობის გაგზავნა',
  'permission.kudos.send.hint': 'კოლეგისთვის მადლობის გადაცემა.',

  'permission.recruiting.read': 'რეკრუტინგის ნახვა',
  'permission.recruiting.read.hint': 'ვაკანსიები, კანდიდატები და ინტერვიუები.',
  'permission.recruiting.manage': 'რეკრუტინგის წარმართვა',
  'permission.recruiting.manage.hint': 'ვაკანსიების გამოქვეყნება, კანდიდატების ეტაპებზე გადაყვანა და ინტერვიუების დანიშვნა.',
  'permission.recruiting.interview': 'კანდიდატებთან გასაუბრება',
  'permission.recruiting.interview.hint': 'კანდიდატთან გასაუბრებაზე დანიშვნა; ჩანს მხოლოდ ის კანდიდატები, ვისთანაც გასაუბრება აქვს დანიშნული, რეზიუმესთან ერთად.',

  'permission.practice.manage': 'სავარჯიშო დარბაზის მართვა',
  'permission.practice.manage.hint': 'გამოგონილი დარბაზის შექმნა, განულება და წაშლა, რომელშიც პერსონალი სწავლობს.',
};
